import React, { useMemo } from 'react';
import { Component } from '../../types';
import { pmTheme } from '../pm/pmTheme';

export interface AssignmentTask {
  title: string;
  phase?: string;
  dueDate?: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  completed: boolean;
}

export interface Assignment {
  id: string;
  name: string;
  role?: string;
  avatar?: string;
  tasks: AssignmentTask[];
  status: 'available' | 'busy' | 'away';
}

export interface AssignmentWidgetProperties {
  assignments: Assignment[];
  layout: 'list' | 'grid' | 'compact';
  showTaskCount: boolean;
  showStatus: boolean;
  groupBy?: 'role' | 'status' | 'phase';
}

export interface AssignmentWidgetProps {
  component: Component;
  isViewerMode?: boolean;
}

type AssignmentStatus = Assignment['status'];
type AssignmentPriority = AssignmentTask['priority'];

const ASSIGNMENT_STATUSES = new Set<string>(['available', 'busy', 'away']);
const ASSIGNMENT_PRIORITIES = new Set<string>(['critical', 'high', 'medium', 'low']);
const ASSIGNMENT_LAYOUTS = new Set<string>(['list', 'grid', 'compact']);
const ASSIGNMENT_GROUP_KEYS = new Set<string>(['role', 'status', 'phase']);

const STATUS_COLORS: Record<AssignmentStatus, string> = {
  available: pmTheme.success,
  busy: pmTheme.warning,
  away: pmTheme.textMuted,
};

const AVATAR_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || '?').toUpperCase();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function isAssignmentStatus(value: unknown): value is AssignmentStatus {
  return typeof value === 'string' && ASSIGNMENT_STATUSES.has(value);
}

function isAssignmentPriority(value: unknown): value is AssignmentPriority {
  return typeof value === 'string' && ASSIGNMENT_PRIORITIES.has(value);
}

function isAssignmentLayout(value: unknown): value is AssignmentWidgetProperties['layout'] {
  return typeof value === 'string' && ASSIGNMENT_LAYOUTS.has(value);
}

function isAssignmentGroupKey(value: unknown): value is NonNullable<AssignmentWidgetProperties['groupBy']> {
  return typeof value === 'string' && ASSIGNMENT_GROUP_KEYS.has(value);
}

function normalizeAssignmentTask(value: unknown): AssignmentTask | null {
  if (!isRecord(value)) {
    return null;
  }

  const title = optionalString(value.title);
  if (!title) {
    return null;
  }

  const task: AssignmentTask = {
    title,
    priority: isAssignmentPriority(value.priority) ? value.priority : 'medium',
    completed: value.completed === true,
  };
  const phase = optionalString(value.phase);
  const dueDate = optionalString(value.dueDate);

  if (phase) task.phase = phase;
  if (dueDate) task.dueDate = dueDate;

  return task;
}

function normalizeAssignment(value: unknown, index: number): Assignment | null {
  if (!isRecord(value)) {
    return null;
  }

  const name = optionalString(value.name);
  if (!name) {
    return null;
  }

  const rawTasks = value.tasks;
  const assignment: Assignment = {
    id: optionalString(value.id) ?? `assignment-${index}`,
    name,
    tasks: Array.isArray(rawTasks)
      ? rawTasks
        .map(normalizeAssignmentTask)
        .filter((task): task is AssignmentTask => task !== null)
      : [],
    status: isAssignmentStatus(value.status) ? value.status : 'available',
  };
  const role = optionalString(value.role);
  const avatar = optionalString(value.avatar);

  if (role) assignment.role = role;
  if (avatar) assignment.avatar = avatar;

  return assignment;
}

export const AssignmentWidget: React.FC<AssignmentWidgetProps> = ({ component, isViewerMode = false }) => {
  const properties = component.properties ?? {};
  const rawAssignments = properties.assignments;
  const assignments = useMemo(
    () => Array.isArray(rawAssignments)
      ? rawAssignments
        .map((assignment, index) => normalizeAssignment(assignment, index))
        .filter((assignment): assignment is Assignment => assignment !== null)
      : [],
    [rawAssignments],
  );
  const layout = isAssignmentLayout(properties.layout) ? properties.layout : 'list';
  const showTaskCount = typeof properties.showTaskCount === 'boolean' ? properties.showTaskCount : true;
  const showStatus = typeof properties.showStatus === 'boolean' ? properties.showStatus : true;
  const groupBy = isAssignmentGroupKey(properties.groupBy) ? properties.groupBy : undefined;

  const groupedAssignments = useMemo(() => {
    if (!groupBy) return { '': assignments };
    const groups: Record<string, Assignment[]> = {};
    assignments.forEach(a => {
      const key = groupBy === 'role' ? (a.role || 'Unassigned') : groupBy === 'status' ? a.status : '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    });
    return groups;
  }, [assignments, groupBy]);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: layout === 'compact' ? 'center' : undefined,
    fontFamily: 'Inter, system-ui, sans-serif',
    overflow: 'auto',
    padding: layout === 'compact' ? '4px 8px' : '12px',
    boxSizing: 'border-box',
    background: pmTheme.surface,
    color: pmTheme.text,
  };

  if (layout === 'compact') {
    return (
      <div style={containerStyle} data-testid="assignment-widget-component" role="region" aria-label="Team assignments">
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          {assignments.map(a => (
            <div key={a.id} style={{ position: 'relative' }} title={`${a.name}${a.role ? ` - ${a.role}` : ''}`}>
              <AvatarCircle name={a.name} avatar={a.avatar} size={32} />
              {showStatus && <StatusDot status={a.status} size={8} />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (layout === 'grid') {
    return (
      <div style={containerStyle} data-testid="assignment-widget-component" role="region" aria-label="Team assignments">
        {Object.entries(groupedAssignments).map(([group, items]) => (
          <div key={group || 'default'}>
            {group && groupBy && (
              <div style={{ fontSize: '12px', fontWeight: 600, color: pmTheme.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {group}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
              {items.map(a => (
                <div key={a.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '12px 8px', borderRadius: '8px', backgroundColor: pmTheme.surfaceSubtle, border: `1px solid ${pmTheme.border}` }}>
                  <div style={{ position: 'relative' }}>
                    <AvatarCircle name={a.name} avatar={a.avatar} size={56} />
                    {showStatus && <StatusDot status={a.status} size={10} />}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: pmTheme.text, textAlign: 'center' }}>{a.name}</div>
                  {a.role && <div style={{ fontSize: '11px', color: pmTheme.textMuted, textAlign: 'center' }}>{a.role}</div>}
                  {showTaskCount && <TaskBadge tasks={a.tasks} />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // List layout (default)
  return (
    <div style={containerStyle} data-testid="assignment-widget-component" role="region" aria-label="Team assignments">
      {Object.entries(groupedAssignments).map(([group, items]) => (
        <div key={group || 'default'}>
          {group && groupBy && (
            <details open style={{ marginBottom: '4px' }}>
              <summary style={{ fontSize: '12px', fontWeight: 600, color: pmTheme.textMuted, cursor: 'pointer', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', userSelect: 'none' }}>
                {group} ({items.length})
              </summary>
              {items.map(a => <ListItem key={a.id} assignment={a} showTaskCount={showTaskCount} showStatus={showStatus} />)}
            </details>
          )}
          {(!group || !groupBy) && items.map(a => <ListItem key={a.id} assignment={a} showTaskCount={showTaskCount} showStatus={showStatus} />)}
        </div>
      ))}
    </div>
  );
};

const ListItem: React.FC<{ assignment: Assignment; showTaskCount: boolean; showStatus: boolean }> = ({ assignment, showTaskCount, showStatus }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '6px', marginBottom: '4px', transition: 'background-color 0.15s ease' }}>
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <AvatarCircle name={assignment.name} avatar={assignment.avatar} size={40} />
      {showStatus && <StatusDot status={assignment.status} size={10} />}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: pmTheme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{assignment.name}</div>
      {assignment.role && <div style={{ fontSize: '12px', color: pmTheme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{assignment.role}</div>}
    </div>
    {showTaskCount && <TaskBadge tasks={assignment.tasks} />}
  </div>
);

const AvatarCircle: React.FC<{ name: string; avatar?: string; size: number }> = ({ name, avatar, size }) => {
  if (avatar) {
    return (
      <img
        src={avatar}
        alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  }
  const bgColor = getAvatarColor(name);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: pmTheme.textInverse,
        fontSize: size * 0.38,
        fontWeight: 600,
        userSelect: 'none',
      }}
      aria-label={name}
    >
      {getInitials(name)}
    </div>
  );
};

const StatusDot: React.FC<{ status: AssignmentStatus; size: number }> = ({ status, size }) => (
  <div
    style={{
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: STATUS_COLORS[status],
      border: `2px solid ${pmTheme.surface}`,
    }}
    title={status}
  />
);

const TaskBadge: React.FC<{ tasks: AssignmentTask[] }> = ({ tasks }) => {
  const safeTasks = tasks || [];
  const completed = safeTasks.filter(t => t.completed).length;
  const total = safeTasks.length;
  return (
    <span style={{
      fontSize: '11px',
      color: pmTheme.textMuted,
      backgroundColor: pmTheme.surfaceMuted,
      padding: '2px 8px',
      borderRadius: '10px',
      whiteSpace: 'nowrap',
    }}>
      {completed}/{total} tasks
    </span>
  );
};

AssignmentWidget.displayName = 'AssignmentWidget';
